// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { AutoSizer, Column, Table, SortDirection } from 'react-virtualized';

import type { Dispatch, NamespaceDefinition } from 'state/types';
import type { SortDirectionType } from 'util/tableUtils';
import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import {
  hideDialogPanel,
  showDialogPanel,
} from 'state/commands/dialogPanelCommands';
import {
  CREATE_XMP_NAMESPACE_PANEL_ID,
  XMP_NAMESPACE_PANEL_ID,
} from 'util/constants';
import { getActivePanelId, getCustomXMPNamespaces } from 'state/selectors';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import {
  getCustomXmpNamespaces as getCustomXMPNamespacesAction,
  removeCustomXmpNamespaces,
} from 'state/commands/catalogCommands';
import {
  getNewSelectionState,
  getSelectedIndices,
  type SelectionState,
  reindexSelectedItems,
} from 'util/tableUtils';
import MultipleSelectionTable from 'view/App/MultipleSelectionTable';
import PageHeader from 'view/Pages/PageHeader';

type Props = {
  activePanelId: ?string,
  customXMPNamespaces: NamespaceDefinition[],
  getCustomXMPNamespacesAction: typeof getCustomXMPNamespacesAction,
  intl: IntlShape,
  hideDialogPanel: typeof hideDialogPanel,
  removeCustomXmpNamespaces: typeof removeCustomXmpNamespaces,
  showDialogPanel: typeof showDialogPanel,
};

type State = {
  selectionState: SelectionState,
  sortBy: string,
  sortDirection: SortDirectionType,
  sortedNamespaceDefinitions: NamespaceDefinition[],
};

class XMPNamespacesDialog extends Component<Props, State> {
  createNamespaceDefinition: Function;
  handleSelectionChanged: Function;
  removeNamespaceDefinitions: Function;
  sort: Function;
  tableRef: ?typeof Table;

  state = {
    selectionState: getNewSelectionState(),
    sortBy: 'namespace',
    sortDirection: SortDirection.ASC,
    sortedNamespaceDefinitions: [],
  };

  constructor() {
    super();

    this.createNamespaceDefinition = this.createNamespaceDefinition.bind(this);
    this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
    this.removeNamespaceDefinitions = this.removeNamespaceDefinitions.bind(
      this
    );
    this.sort = this.sort.bind(this);
  }

  componentDidMount() {
    this.props.getCustomXMPNamespacesAction();
  }

  componentWillReceiveProps(nextProps, stateProps) {
    if (this.props.customXMPNamespaces !== nextProps.customXMPNamespaces) {
      const sortedNamespaceDefinitions = this.sortNamespaceDefinitions(
        nextProps.customXMPNamespaces,
        this.state.sortBy,
        this.state.sortDirection
      );
      this.setState({
        sortedNamespaceDefinitions,
        selectionState: reindexSelectedItems(
          sortedNamespaceDefinitions,
          this.state.selectionState
        ),
      });
    }
  }

  handleSelectionChanged(selectionState: SelectionState) {
    this.setState({ selectionState });
  }

  sortNamespaceDefinitions(
    namespaceDefinitions: NamespaceDefinition[],
    sortBy: string,
    sortDirection: SortDirection
  ) {
    const sorted = namespaceDefinitions.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      return aVal.localeCompare(bVal);
    });

    return sortDirection === SortDirection.ASC ? sorted : sorted.reverse();
  }

  sort({ sortBy, sortDirection }) {
    const sortedNamespaceDefinitions = this.sortNamespaceDefinitions(
      this.props.customXMPNamespaces,
      sortBy,
      sortDirection
    );
    this.setState({
      selectionState: reindexSelectedItems(
        sortedNamespaceDefinitions,
        this.state.selectionState
      ),
      sortedNamespaceDefinitions,
      sortBy,
      sortDirection,
    });
  }

  removeNamespaceDefinitions() {
    const namespaceIds = getSelectedIndices(this.state.selectionState).map(
      idx => this.state.sortedNamespaceDefinitions[idx].id
    );
    if (namespaceIds.length) {
      this.props.removeCustomXmpNamespaces(namespaceIds);
    }
  }

  createNamespaceDefinition() {
    this.props.showDialogPanel(CREATE_XMP_NAMESPACE_PANEL_ID);
  }

  renderPanelHeader() {
    const {
      intl: { formatMessage },
    } = this.props;

    return (
      <div data-test-name="dialog-panel-modal">
        <PageHeader
          deleteDisabled={
            getSelectedIndices(this.state.selectionState).length === 0
          }
          onClickAdd={this.createNamespaceDefinition}
          onClickDelete={this.removeNamespaceDefinitions}
          title={formatMessage(
            localizedStrings.FIELD_ACTION_MANAGE_CUSTOM_XMP_NAMESPACES
          )}
        />
      </div>
    );
  }

  render() {
    const {
      activePanelId,
      intl: { formatMessage },
    } = this.props;
    const {
      selectionState,
      sortBy,
      sortDirection,
      sortedNamespaceDefinitions,
    } = this.state;

    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '600px', height: null }}
        hideDialogPanel={this.props.hideDialogPanel}
        panelId={XMP_NAMESPACE_PANEL_ID}
        panelHeader={this.renderPanelHeader()}
        showCancel={false}
        customButtonText={formatMessage(localizedStrings.OK)}
      >
        <div style={{ height: '300px', width: '600px' }}>
          <AutoSizer>
            {({ height, width }) => (
              <MultipleSelectionTable
                headerHeight={40}
                height={height}
                onSelectionChanged={this.handleSelectionChanged}
                rowHeight={30}
                selectionState={selectionState}
                singleSelectionOnly
                sort={this.sort}
                sortBy={sortBy}
                sortDirection={sortDirection}
                sortedItems={sortedNamespaceDefinitions}
                width={width}
              >
                <Column
                  className="list-item"
                  dataKey="preferredAbbreviation"
                  label={formatMessage(
                    localizedStrings.FIELD_NAMESPACE_ABBREVIATION
                  )}
                  width={300}
                />
                <Column
                  className="list-item"
                  label={formatMessage(localizedStrings.FIELD_NAMESPACE_URI)}
                  dataKey="namespace"
                  width={300}
                />
              </MultipleSelectionTable>
            )}
          </AutoSizer>
        </div>
      </DialogPanel>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    customXMPNamespaces: getCustomXMPNamespaces(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      getCustomXMPNamespacesAction,
      hideDialogPanel,
      removeCustomXmpNamespaces,
      showDialogPanel,
    })
)(injectIntl(XMPNamespacesDialog));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/XMPNamespacesDialog.js